import { IUrlPattern } from 'reduxState/store/campaigns/types';
import { KeyboardEvent } from 'react';

export const validateCampaignUrlPatterns = (patterns: string[] | IUrlPattern[]): boolean => {
  const urlPatterns = patterns.map(pattern => {
    if (typeof pattern === 'string') return pattern;
    return pattern.pattern;
  });
  if (urlPatterns.length === 0) return true;

  return urlPatterns.every(pattern => {
    if (pattern.includes('*')) {
      // Validate wildcard URLs with more flexible pattern matching
      // Supports patterns like:
      // "*://example.org/*", "*://*.example.org/*", "https://*.example.org/*", "https://example.org/*/path", "*.example.org"
      const wildcardRegex = /^(\*|https?):\/\/((\*\.)?[a-zA-Z0-9.-]+|\*)(\/.*)?(\*)?$|^\*\.[a-zA-Z0-9.-]+$/;
      return wildcardRegex.test(pattern);
    }

    try {
      new URL(pattern);
      return true;
    } catch (error) {
      return false;
    }
  });
};

export const isCampaignDestinationUrlValid = (destinationURL: string): boolean => {
  if (!destinationURL) return true;

  try {
    new URL(destinationURL);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Processes input value for both single and bulk modes
 * @param inputValue - The raw input string
 * @param formMode - Whether processing single or bulk input
 * @param addHandler - Function to call for each processed value
 * @param clearInput - Function to clear the input after processing
 */
export const processInputValue = (
  inputValue: string,
  formMode: 'single' | 'bulk',
  addHandler: (value: string) => void,
  clearInput: () => void,
) => {
  if (!inputValue.trim()) return;

  if (formMode === 'bulk') {
    const values = inputValue.split(',');
    values.forEach(val => {
      const trimmedValue = val.trim().replace(/['"`]/g, '');
      if (trimmedValue) {
        addHandler(trimmedValue);
      }
    });
  } else {
    const trimmedValue = inputValue.trim().replace(/['"`]/g, '');
    if (trimmedValue) {
      addHandler(trimmedValue);
    }
  }
  clearInput();
};

/**
 * Creates a keyboard event handler that triggers a callback on Enter key press
 * @param onEnter - Function to call when Enter is pressed
 * @returns Keyboard event handler function
 */
export const createEnterKeyHandler = (onEnter: () => void) => (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    onEnter();
  }
};
